/**
 * `/student/progress` — per-course progress summary.
 *
 * Mock numbers only; nothing here is sourced from a real LMS.
 */

import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';

export function StudentProgress() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.listStudentProgress(token),
    [client, token],
  );

  if (state.status === 'loading') {
    return <LoadingState label="Loading progress…" />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.data.length === 0) {
    return (
      <EmptyState
        title="No progress to show yet"
        description="Once you finish a mock assignment, your progress will appear here."
      />
    );
  }

  return (
    <section className="page" data-testid="student-progress">
      <header className="page__header">
        <h1>Progress</h1>
        <p className="muted">
          Per-course snapshot. Numbers are computed from mock fixtures — no
          real LMS data is loaded.
        </p>
      </header>

      <ul className="list" aria-label="Per-course progress">
        {state.data.map((entry) => (
          <li
            key={entry.courseId}
            className="list__item"
            data-testid="student-progress-row"
          >
            <div className="list__row">
              <div className="list__title">
                <h2>{entry.courseName}</h2>
                <p className="muted">
                  {entry.assignmentsCompleted} of {entry.assignmentsTotal}{' '}
                  assignments complete
                  {entry.lastActivityAt
                    ? ` · last activity ${formatDate(entry.lastActivityAt)}`
                    : ' · no activity yet'}
                </p>
              </div>
              <div
                className="progress progress--wide"
                role="progressbar"
                aria-valuenow={entry.progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${entry.courseName}: ${entry.progressPercent}% complete`}
              >
                <div
                  className="progress__bar"
                  style={{ width: `${clampPercent(entry.progressPercent)}%` }}
                />
                <span className="progress__label">
                  {clampPercent(entry.progressPercent)}%
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
